// Import the Firebase Functions client SDK
import { getFunctions, httpsCallable } from "https://www.gstatic.com/firebasejs/10.12.2/firebase-functions.js";

// Reference to the callable function (initialized later)
let getLeaderboardFunction;

/**
 * Fetches leaderboard data via a Cloud Function.
 * @param {number} [limit=10] - Number of entries to fetch.
 * @returns {Promise<Array<object>>} A promise that resolves with the leaderboard data array or rejects on error.
 */
export async function fetchLeaderboardData(limit = 10) {
  // Initialize the Cloud Function reference if not already done
  if (!getLeaderboardFunction) {
      try {
          const functions = getFunctions(); // Get default Functions instance
          getLeaderboardFunction = httpsCallable(functions, 'getLeaderboardCallable');
      } catch (error) {
          console.error("fetchLeaderboardData: Error getting Firebase Functions instance:", error);
          throw new Error("Error initializing leaderboard service."); // Propagate error
      }
  }

  try {
    // Call the Cloud Function
    console.log(`fetchLeaderboardData: Calling getLeaderboardCallable Cloud Function with limit ${limit}...`);
    const result = await getLeaderboardFunction({ limit: limit }); // Pass limit in data object
    const leaderboardData = result.data?.leaderboard; // Data is nested under 'leaderboard'
    console.log("fetchLeaderboardData: Received leaderboard data from Cloud Function:", leaderboardData);

    if (!leaderboardData) {
        console.warn("fetchLeaderboardData: No leaderboard data received from function call, returning empty array.");
        return []; // Return empty array instead of throwing error
    } else if (!Array.isArray(leaderboardData)) {
        console.error("fetchLeaderboardData: Invalid data format received (expected array):", leaderboardData);
        throw new Error("Invalid data format received for leaderboard.");
    }

    return leaderboardData; // Return the fetched data array

  } catch (error) {
    console.error("fetchLeaderboardData: Error calling Cloud Function:", error);
    let errorMessage = "Could not load leaderboard data at this time.";
     if (error.code === 'unauthenticated') {
         errorMessage = "Authentication error retrieving leaderboard.";
     } else if (error.message) {
         // Use error message if available, otherwise stick to generic
         errorMessage = `Error fetching leaderboard: ${error.message}`; 
     }
    // Re-throw a new error with a more user-friendly or specific message
    throw new Error(errorMessage); 
  }
}

/**
 * DEPRECATED: Updates the HTML section directly.
 * Use fetchLeaderboardData and update UI separately.
 * @param {number} [limit=10] - Number of entries to fetch.
 */
export async function displayLeaderboard(limit = 10) {
  // Target the content div
  const leaderboardContent = document.getElementById('leaderboard-content'); // This ID might not exist anymore
  if (!leaderboardContent) {
    console.warn("displayLeaderboard (DEPRECATED): Could not find #leaderboard-content element.");
    // Attempt to fetch and return data anyway, maybe caller can use it?
    try {
      const data = await fetchLeaderboardData(limit);
      // We can't display it, but log it
      console.log("displayLeaderboard (DEPRECATED): Fetched data but couldn't display:", data);
    } catch (error) {
      console.error("displayLeaderboard (DEPRECATED): Error fetching data:", error);
    }
    return; 
  }

  // Show loading state in the content div
  leaderboardContent.innerHTML = '<p>Loading leaderboard...</p>';

  try {
    // Call the new function to get data
    const leaderboardData = await fetchLeaderboardData(limit);

    // Generate HTML for the leaderboard content (NO H2 here)
    let leaderboardHtml = '';
    if (leaderboardData.length === 0) {
      leaderboardHtml += '<p>No player data available yet.</p>';
    } else {
      leaderboardHtml += '<ol>'; // Use ordered list for ranking
      leaderboardData.forEach(player => {
        // Make sure the expected fields exist
        const displayName = player.displayName || 'Unknown';
        const wins = player.wins ?? 'N/A'; // Use nullish coalescing
        const gamesPlayed = player.gamesPlayed ?? 'N/A';
        leaderboardHtml += `<li>${displayName} - Wins: ${wins} (Played: ${gamesPlayed})</li>`;
      });
      leaderboardHtml += '</ol>';
    }

    // Update the content div
    leaderboardContent.innerHTML = leaderboardHtml;
    console.log("displayLeaderboard (DEPRECATED): Leaderboard displayed successfully.");

  } catch (error) {
    console.error("displayLeaderboard (DEPRECATED): Error displaying leaderboard:", error);
    // Update content div with error message
    leaderboardContent.innerHTML = `<p>${error.message || 'Could not load leaderboard.'}</p>`;
  }
} 